import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConCreateRequest,
  ConListItem,
  ConListResponse,
  ConResponse,
  ConUpdateRequest,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: ['con', 'list'] as const,
  one: (id: string) => ['con', 'one', id] as const,
};

export function useConList(query: { status?: string; periodId?: string } = {}) {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.periodId) params.set('periodId', query.periodId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: [...QK.list, query],
    queryFn: () => api<ConListResponse>(`/api/documents/con${qs}`),
  });
}

export function useCon(id: string | null) {
  return useQuery({
    queryKey: QK.one(id ?? 'none'),
    queryFn: () => api<ConResponse>(`/api/documents/con/${id}`),
    enabled: !!id,
  });
}

export function useCreateCon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConCreateRequest) =>
      api<ConResponse>('/api/documents/con', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useUpdateCon(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConUpdateRequest) =>
      api<ConResponse>(`/api/documents/con/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
      void qc.invalidateQueries({ queryKey: QK.one(id) });
    },
  });
}

export function useCloseCon(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<ConResponse>(`/api/documents/con/${id}/close`, { method: 'POST', body: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
      void qc.invalidateQueries({ queryKey: QK.one(id) });
    },
  });
}

/**
 * Scarica PDF etichette per un CON. Usa fetch+blob per passare auth header
 * (window.open non lo supporterebbe). Apre il PDF in una nuova tab.
 */
export async function downloadConLabels(conId: string): Promise<void> {
  const { useAuthStore } = await import('../stores/auth-store');
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`/api/finished-boxes/labels.pdf?conId=${encodeURIComponent(conId)}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Errore stampa etichette (${res.status}): ${txt.slice(0, 200)}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function useDeleteCon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/documents/con/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

// =========================================================
// LotPosition (read-only) per il dropdown CON.lotPositionId
// =========================================================

export interface LotPositionItem {
  id: string;
  lotId: string;
  lotCode: string;
  lotStatus: string;
  productId: string;
  productCode: string;
  productName: string;
  productDriveLegendId: number | null;
  residualQty: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface LotPositionListResponse {
  items: LotPositionItem[];
  total: number;
  page: number;
  pageSize: number;
}

export function useLotPositions(query: {
  search?: string;
  productId?: string;
  minResidualQty?: number;
  pageSize?: number;
}) {
  const params = new URLSearchParams();
  if (query.search) params.set('search', query.search);
  if (query.productId) params.set('productId', query.productId);
  if (query.minResidualQty !== undefined) params.set('minResidualQty', String(query.minResidualQty));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  return useQuery({
    queryKey: ['lot-positions', 'list', query],
    queryFn: () => api<LotPositionListResponse>(`/api/lot-positions?${params.toString()}`),
  });
}

export type { ConListItem };
